import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Property } from '../App';
import GoogleSatelliteMapView from './GoogleSatelliteMapView';
import './PropertyMapView.css';

interface PropertyMapViewProps {
  properties: Property[];
  onSelectProperty: (property: Property) => void;
  focusPropertyId?: string | null;
}

interface MapPin {
  id: string;
  latitude: number;
  longitude: number;
  tenant: string;
  address: string;
  city: string;
  state: string;
}

function parseCoordinate(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function pinToProperty(pin: MapPin): Property {
  return {
    id: pin.id,
    latitude: pin.latitude,
    longitude: pin.longitude,
    tenant: pin.tenant,
    address: pin.address,
    city: pin.city,
    state: pin.state,
    createdAt: '',
  };
}

const PropertyMapView: React.FC<PropertyMapViewProps> = ({ properties, onSelectProperty, focusPropertyId: externalFocusId }) => {
  const [mappedFocusIndex, setMappedFocusIndex] = useState(-1);
  const [mapPins, setMapPins] = useState<MapPin[]>([]);
  const [totalWithout, setTotalWithout] = useState(0);
  const [totalProperties, setTotalProperties] = useState(0);
  const [isLoadingPins, setIsLoadingPins] = useState(true);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState('');

  const fetchMapPins = useCallback(async () => {
    try {
      setIsLoadingPins(true);
      const resp = await fetch('/api/properties/map-pins');
      if (resp.ok) {
        const data = await resp.json();
        setMapPins(data.pins || []);
        setTotalWithout(data.totalWithoutCoordinates || 0);
        setTotalProperties(data.totalProperties || 0);
      }
    } catch (e) {
      console.error('Error fetching map pins:', e);
    } finally {
      setIsLoadingPins(false);
    }
  }, []);

  useEffect(() => {
    fetchMapPins();
  }, [fetchMapPins]);

  const mapProperties = useMemo(() => {
    const result: Property[] = [];
    const seen = new Set<string>();

    for (const p of properties) {
      const lat = parseCoordinate(p.latitude) ?? parseCoordinate(p.reportAllParcel?.latitude);
      const lng = parseCoordinate(p.longitude) ?? parseCoordinate(p.reportAllParcel?.longitude);
      if (lat !== null && lng !== null) {
        result.push(p);
        seen.add(p.id);
      }
    }

    for (const pin of mapPins) {
      if (!seen.has(pin.id)) {
        result.push(pinToProperty(pin));
      }
    }
    return result;
  }, [properties, mapPins]);

  const geocodedCount = mapProperties.length;

  const cycleMappedFocus = () => {
    if (mapProperties.length === 0) return;
    const nextIndex = (mappedFocusIndex + 1) % mapProperties.length;
    setMappedFocusIndex(nextIndex);
  };

  const focusPropertyId = externalFocusId
    || (mappedFocusIndex >= 0 && mappedFocusIndex < mapProperties.length
      ? mapProperties[mappedFocusIndex].id
      : null);

  const handleSelectProperty = (property: Property) => {
    const full = properties.find((p) => p.id === property.id);
    onSelectProperty(full || property);
  };

  const handleGeocodeMissing = async () => {
    setIsGeocoding(true);
    setGeocodeProgress('Starting...');
    let totalUpdated = 0;
    let remaining = totalWithout;

    try {
      while (remaining > 0) {
        setGeocodeProgress(`Geocoding... ${totalUpdated} done, ~${remaining} remaining`);
        const resp = await fetch('/api/properties/geocode-missing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ batchSize: 500 }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || 'Geocoding failed');
        }
        const data = await resp.json();
        totalUpdated += data.updated || 0;
        remaining = data.remainingWithoutCoordinates ?? 0;

        if ((data.updated || 0) === 0) break;
      }
      setGeocodeProgress(`Done! Geocoded ${totalUpdated} properties.`);
      await fetchMapPins();
    } catch (e: any) {
      setGeocodeProgress(`Error: ${e.message || 'Geocoding failed'}`);
    } finally {
      setIsGeocoding(false);
    }
  };

  return (
    <div className="property-map-shell">
      <div className="property-map-meta">
        <span>Google Satellite</span>
        <button className="map-mapped-btn" onClick={cycleMappedFocus} title="Jump to next mapped property">
          {geocodedCount.toLocaleString()} mapped
        </button>
        {totalWithout > 0 && !isGeocoding ? (
          <button className="map-geocode-btn" onClick={handleGeocodeMissing} title="Geocode properties without coordinates">
            Geocode {totalWithout.toLocaleString()} missing
          </button>
        ) : null}
        {isGeocoding ? <span className="map-geocode-progress">{geocodeProgress}</span> : null}
        {!isGeocoding && geocodeProgress.startsWith('Done') ? <span className="map-geocode-done">{geocodeProgress}</span> : null}
      </div>
      <GoogleSatelliteMapView
        properties={mapProperties}
        onSelectProperty={handleSelectProperty}
        focusPropertyId={focusPropertyId}
      />
      {isLoadingPins ? (
        <div className="property-map-empty">
          <h3>Loading map data...</h3>
        </div>
      ) : totalProperties > 0 && geocodedCount === 0 ? (
        <div className="property-map-empty">
          <h3>Map loaded, no pins yet</h3>
          <p>Properties need coordinates to appear as markers.</p>
          {totalWithout > 0 && !isGeocoding ? (
            <button className="map-geocode-btn-large" onClick={handleGeocodeMissing}>
              Geocode {totalWithout.toLocaleString()} Properties
            </button>
          ) : null}
          {isGeocoding ? <p className="map-geocode-progress">{geocodeProgress}</p> : null}
        </div>
      ) : null}
    </div>
  );
};

export default PropertyMapView;
